"use client";

import { type InputHTMLAttributes, useRef, type FC, type Ref } from "react";
import { mergeClasses } from "@huub-web/utils";
import type { RecipeVariants } from "@vanilla-extract/recipes";

import type { ActiveProp, ClassNameProp, ValidatableProps } from "../../props";
import { useFormValidation } from "../../hooks/useFormValidation";
import { mergeRefs } from "../../utils";
import { Flex } from "../Flex";
import { Text } from "../Text";
import { Icon } from "../Icon";
import { Reveal } from "../Reveal";
import { Badge } from "../Badge";

import {
  containerStyle,
  checkboxItemStyle,
  checkboxInputStyle,
  checkboxBoxStyle,
  checkIconStyle,
  labelStyle,
  successBadgeStyle,
} from "./CheckboxGroup.css";
import { borders } from "../../theme/css/borders.css";
import { focusWithin } from "../../theme/css/focusVisible.css";

export type CheckboxOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

export type CheckboxGroupChangeHandler = (value: string[]) => void;

export type CheckboxGroupProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "onChange" | "value" | "type" | "required"
> &
  RecipeVariants<typeof checkboxItemStyle> &
  ValidatableProps &
  ActiveProp &
  ClassNameProp & {
    /**
     * Accepted but not spread to individual inputs — validation is handled
     * by the `validation` prop via useFormValidation ("at least one selected").
     */
    required?: boolean;
    /**
     * The current selected values
     */
    value?: string[];
    /**
     * Array of checkbox options
     */
    options: CheckboxOption[];
    /**
     * Orientation of the checkboxes
     */
    orientation?: "horizontal" | "vertical";
    /**
     * Whether to highlight the checkboxes
     */
    highlight?: boolean;
    /**
     * Whether to show success state
     */
    success?: boolean;
    /**
     * Handler for value change
     */
    onChange?: CheckboxGroupChangeHandler;
    /**
     * Ref forwarded to the root element
     */
    ref?: Ref<HTMLDivElement>;
  };

/**
 * CheckboxGroup component for selecting multiple options from a list.
 */
export const CheckboxGroup: FC<CheckboxGroupProps> = ({
  id: idProp,
  disabled: disabledProp,
  error: errorProp,
  validation,
  name: nameProp,
  orientation = "vertical",
  highlight,
  success,
  value = [],
  options,
  active,
  onChange,
  className,
  required: _required,
  ref,
  ...props
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const { id, error, disabled } = useFormValidation(value, {
    id: idProp,
    error: errorProp,
    disabled: disabledProp,
    validation,
    name: nameProp,
    ref: inputRef,
  });

  const handleChange = (optionValue: string) => {
    if (disabled) {
      return;
    }

    const newValue = [...value];
    const index = newValue.indexOf(optionValue);

    if (index === -1) {
      newValue.push(optionValue);
    } else {
      newValue.splice(index, 1);
    }

    onChange?.(newValue);
  };

  return (
    <Flex
      ref={ref}
      className={mergeClasses(containerStyle({ orientation }), className)}
      direction={orientation === "horizontal" ? "row" : "column"}
      role="group"
      aria-label="Checkbox group"
      {...(active && { "aria-active": true })}
    >
      {options.map((option, index) => {
        const optionID = index === 0 ? id : `${id}-${index}`;
        const isChecked = value.includes(option.value);
        const isDisabled = disabled || option.disabled;

        return (
          <Flex
            key={option.value}
            className={checkboxItemStyle({
              checked: isChecked,
              disabled: isDisabled,
              success,
              highlight,
              error,
            })}
            align="flex-start"
          >
            <label
              htmlFor={optionID}
              className={mergeClasses(
                checkboxBoxStyle({
                  checked: isChecked,
                  disabled: isDisabled,
                  success,
                  highlight,
                  error,
                }),
                focusWithin,
                borders.default,
              )}
              {...(isDisabled && { "aria-disabled": true })}
            >
              <input
                ref={index === 0 ? mergeRefs(inputRef) : undefined}
                id={optionID}
                type="checkbox"
                name={nameProp || id}
                value={option.value}
                checked={isChecked}
                className={checkboxInputStyle}
                onChange={() => handleChange(option.value)}
                disabled={isDisabled}
                {...props}
              />
              <Icon
                icon="checkThick"
                className={checkIconStyle({ checked: isChecked })}
                size="xs"
              />
            </label>

            <Text
              as="label"
              htmlFor={optionID}
              className={labelStyle({ disabled: isDisabled })}
              variant="bodyS"
              color="currentColor75"
            >
              {option.label}
            </Text>

            {isChecked && success && (
              <Reveal>
                <Badge
                  variant="success"
                  size="xs"
                  value={<Icon icon="checkThick" size="xs" />}
                  className={successBadgeStyle}
                  inline
                />
              </Reveal>
            )}
          </Flex>
        );
      })}
    </Flex>
  );
};
